import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import { getNDList } from 'services/nd'
import { NDPayload } from 'services/nd/interface'
import { AppThunk } from 'store/store'
import { SortFilterType } from 'components'
import { Negotiation, Pagination, Statistics } from './Types/NegotiationType'

type InitialState = {
  loadingNDs: boolean
  negotiationsList: Negotiation[]
  negotiationsStatistics: Statistics
  sortFilter: SortFilterType
  pagination: Pagination
}
const initialState: InitialState = {
  loadingNDs: true,
  negotiationsList: [],
  negotiationsStatistics: {} as Statistics,
  sortFilter: {} as SortFilterType,
  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0
  }
}

const negotiations = createSlice({
  name: 'negotiations',
  initialState,
  reducers: {
    onRequest(state) {
      state.loadingNDs = true
    },
    afterRequest(state) {
      state.loadingNDs = false
    },
    setNegotiationsList(state, action: PayloadAction<Negotiation[] | undefined>) {
      if (action.payload) {
        state.negotiationsList = action.payload
      } else {
        state.negotiationsList = []
      }
    },
    setNegotiationStatiscs(state, action: PayloadAction<Statistics>) {
      state.negotiationsStatistics = action.payload
    },
    setSortFilter(state, action: PayloadAction<SortFilterType>) {
      state.sortFilter = action.payload
    },
    setPagination(state, action: PayloadAction<Pagination>) {
      state.pagination = action.payload
    },
    setCurrentPage(state, action: PayloadAction<number>) {
      state.pagination.currentPage = action.payload
    },
    resetPagination(state) {
      state.pagination = {
        currentPage: 1,
        pageSize: 10,
        totalItems: 0,
        totalPages: 0
      }
    }
  }
})

export const negotiationsContext = negotiations.reducer
export const {
  onRequest,
  afterRequest,
  setNegotiationsList,
  setNegotiationStatiscs,
  setSortFilter,
  setPagination,
  setCurrentPage,
  resetPagination
} = negotiations.actions

export const asyncGetNegotiations =
  (payload: NDPayload, page: number, sortFilter: SortFilterType): AppThunk =>
  async (dispatch, getState) => {
    dispatch(onRequest())
    const { pagination } = getState().negotiations
    const pageSize = 10
    try {
      const negotiationsData = await getNDList(payload, {
        page: page - 1,
        limit: pageSize,
        ...sortFilter
      })

      const hasNegotiations =
        Object.keys(negotiationsData).includes('NegociacoesDiferenciadasAgrupadas') &&
        negotiationsData.NegociacoesDiferenciadasAgrupadas.length > 0

      if (hasNegotiations) {
        const { NegociacoesDiferenciadasAgrupadas: negotiationsList, ...Statistics } =
          negotiationsData

        const totalItems = negotiationsList[0].RowsCount
        const totalPages = Math.ceil(totalItems / pageSize)
        const updatedPagination = {
          ...pagination,
          currentPage: page,
          pageSize,
          totalItems,
          totalPages
        }

        dispatch(setPagination(updatedPagination))
        dispatch(setNegotiationsList(negotiationsList))
        dispatch(setNegotiationStatiscs(Statistics))
      } else {
        dispatch(setPagination({ ...pagination, currentPage: 1 }))
        dispatch(setNegotiationsList())
        dispatch(setNegotiationStatiscs({} as Statistics))
      }
    } catch (error) {
      toast.error('Erro ao obter negociações.')
    } finally {
      dispatch(afterRequest())
    }
  }
